"use client";

import { useMemo, useRef, useSyncExternalStore } from "react";
import { useMutation } from "@tanstack/react-query";

import { EMPTY_GLYPH_MEMBERSHIP } from "@/lib/wallet/glyph-wallet-membership";

import type { WalletState } from "@/lib/wallet/context";
import type {
  GlyphSessionApi,
  PrivySolanaClientType,
} from "@/lib/wallet/glyph-account";

const noMembership = () => EMPTY_GLYPH_MEMBERSHIP;
const noSubscription = () => () => {};

/** Explicit shared Glyph link; never a wallet proof or asset connection. */
export function useAddGlyphWallet({
  session,
  address,
  solana,
  clientType,
  enabled,
  currentBinding,
}: {
  session: GlyphSessionApi | null;
  address: string | null;
  solana: WalletState["solana"];
  clientType: PrivySolanaClientType | undefined;
  enabled: boolean;
  currentBinding: () => () => boolean;
}) {
  const scope = useMemo(
    () => ({ session, address, solana, clientType, enabled }),
    [session, address, solana, clientType, enabled]
  );
  const pending = useRef<(() => boolean) | null>(null);
  const mutation = useMutation({
    mutationFn: async ({
      userId,
      wallet,
      current,
    }: {
      userId: string;
      wallet: string;
      current: () => boolean;
      scope: typeof scope;
    }) => {
      let open = true;
      const valid = () => open && current();
      try {
        const sign = solana?.signMessage;
        if (
          !session?.authenticated ||
          session.privyUserId !== userId ||
          !sign ||
          !valid()
        )
          return null;
        await session.addSolanaWalletToGlyph(
          wallet,
          async (bytes) => {
            if (!valid()) throw new Error("The selected wallet changed.");
            const signed = await sign(bytes);
            if (!valid()) throw new Error("The selected wallet changed.");
            return signed instanceof Uint8Array ? signed : signed.signature;
          },
          clientType,
          valid
        );
        return valid() ? { userId, wallet } : null;
      } finally {
        open = false;
        if (pending.current === current) pending.current = null;
      }
    },
  });
  const active = enabled && mutation.variables?.scope === scope;
  const userId = session?.privyUserId;
  const membership = useSyncExternalStore(
    session?.walletMembership?.subscribe ?? noSubscription,
    session?.walletMembership?.getSnapshot ?? noMembership,
    noMembership
  );
  const listed = Boolean(
    enabled &&
    session?.authenticated &&
    address &&
    membership.userId === userId &&
    membership.addresses.includes(address)
  );
  return {
    reset: mutation.reset,
    error: !listed && active ? mutation.error : null,
    pending: !listed && active && mutation.isPending,
    added: listed,
    /** Privy's wallet list for this account has not arrived, so `added` is not an answer yet. */
    membershipLoading: Boolean(
      enabled &&
      session?.authenticated &&
      userId &&
      membership.userId !== userId
    ),
    add:
      enabled &&
      session?.authenticated &&
      !listed &&
      userId &&
      address &&
      solana?.signMessage
        ? () => {
            if (pending.current?.()) return;
            const current = currentBinding();
            if (!current()) return;
            pending.current = current;
            mutation.mutate({
              userId,
              wallet: address,
              current,
              scope,
            });
          }
        : undefined,
  };
}
